// ============= Imports =============

import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

interface GetDocumentsParams {
  workflowId: number;
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser: string | null;
}

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/Document/workflow/';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';

/**
 * Creates headers for API request
 * @param token Authentication token
 * @param isFileUpload Whether this is a file upload request
 * @returns Headers object
 */
const createHeaders = (
  token: string,
  spoofedUser: string | null
): Record<string, string> => {
  const baseHeaders: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(spoofedUser && {
      'x-Spoof-User': spoofedUser,
    }),
  } as Record<string, string>;

  return baseHeaders;
};

// ============= Main Function =============
/**
 * Fetches users from Microsoft API based on search criteria
 * @param getAccessToken Function to acquire access token
 * @param searchValue Search string to filter users
 * @returns Promise with API response containing user data or error
 */
export const getDocuments = async ({
  workflowId,
  getAccessToken,
  spoofedUser,
}: GetDocumentsParams) => {
  try {
    console.log('🔄 Fetching documents...');

    // Token acquisition with required scopes
    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}${workflowId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // Build headers so that for FormData we let the browser set the correct Content-Type with boundary
    const headers = createHeaders(token, spoofedUser || null);
    // API request
    const response = await fetch(convertURI, {
      method: 'GET',
      headers,
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Documents fetched successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error submitting document:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
